import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import React, { useCallback, useState } from 'react';

import { coproTypes } from '../../../../utils/copros';
import { generateEntryKey } from '../../../../utils/nameMatching';
import { showToast } from '../../../common/Notification/ToastrNotif.module';
import type { NotInRnicDecision } from '../ImportDetail.module';

interface TypeSelectionStepProps {
  entries: NotInRnicDecision[];
  totalVisible: number;
  search: string;
  onSearchChange: (value: string) => void;
  onClearSearch: () => void;
  filteredCount: number;
  onExport: () => void;
  onDecision: (entryKey: string, decision: 'force' | 'skip', forcedType?: number) => void;
  onBulkDecision: (decision: 'force' | 'skip', forcedType?: number) => void;
  editingEntryKey: string | null;
  editingImmatValue: string;
  onStartEditImmat: (entryKey: string, currentImmat: string) => void;
  onConfirmEdit: (entryKey: string) => void;
  onCancelEdit: () => void;
  setEditingImmatValue: (value: string) => void;
  fileAddressMap: Map<string, string>;
}

const TypeSelectionStep: React.FC<TypeSelectionStepProps> = ({
  entries,
  totalVisible,
  search,
  onSearchChange,
  onClearSearch,
  filteredCount,
  onExport,
  onDecision,
  onBulkDecision,
  editingEntryKey,
  editingImmatValue,
  onStartEditImmat,
  onConfirmEdit,
  onCancelEdit,
  setEditingImmatValue,
  fileAddressMap,
}) => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const handleCopy = useCallback(async(text: string, key: string) => {
    if (!text) {return;}
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      showToast('error', 'Impossible de copier dans le presse-papiers');
    }
  }, []);

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-red-200 bg-red-50">
      <div className="flex-shrink-0 border-b border-red-200 bg-red-50 p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-base font-semibold text-red-800">
            <ExclamationTriangleIcon className="h-5 w-5" />
            Non trouvé dans RNIC ({totalVisible})
          </h3>
          <div className="flex gap-2">
            <button
              onClick={onExport}
              disabled={totalVisible === 0}
              className="flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              title="Exporter toutes les entrées"
            >
              <ArrowDownTrayIcon className="h-3 w-3" />
              Exporter
            </button>
            <button
              onClick={() => onBulkDecision('force', 1)}
              className="flex items-center gap-1 rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-700 transition-colors hover:bg-green-200"
            >
              Tout forcer copro
            </button>
            <button
              onClick={() => onBulkDecision('skip')}
              className="rounded bg-red-100 px-2 py-1 text-xs font-medium text-red-700 transition-colors hover:bg-red-200"
            >
              Tout ignorer
            </button>
          </div>
        </div>
          <p className="mb-3 text-xs text-red-700">
            Corrigez l'immatriculation, forcez comme copropriété ou ignorez.
          </p>

        <div className="relative">
          <MagnifyingGlassIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Rechercher..."
            className="w-full rounded-lg border border-gray-300 py-1.5 pr-4 pl-9 text-sm focus:border-red-400 focus:ring-2 focus:ring-red-300 focus:outline-none"
          />
          {search && (
            <button
              onClick={onClearSearch}
              className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <XCircleIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        {search && (
          <p className="mt-1 text-xs text-gray-500">
            {filteredCount} résultat(s) sur {totalVisible}
          </p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {entries.length > 0 ? (
          <table className="min-w-full divide-y divide-red-200">
            <thead className="sticky top-0 bg-red-100">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-red-800 uppercase">Immat.</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-red-800 uppercase">Nom</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-red-800 uppercase">Adresse fichier</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-red-800 uppercase">Raison</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-red-800 uppercase">Type</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-red-800 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {entries.map((entry) => {
                const entryKey = generateEntryKey(entry.immatriculation, entry.name);
                const isEditing = editingEntryKey === entryKey;
                const displayImmat = entry.correctedImmatriculation || entry.immatriculation;
                const address = fileAddressMap.get(entryKey) || '';

                return (
                  <tr key={entryKey}>
                    <td className="px-3 py-2 font-mono text-xs text-gray-600">
                      {isEditing ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={editingImmatValue}
                            onChange={(e) => setEditingImmatValue(e.target.value)}
                            className="w-24 rounded border border-gray-300 px-1 py-0.5 text-xs focus:ring-2 focus:ring-rgpd-blue-dark focus:outline-none"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                onConfirmEdit(entryKey);
                              } else if (e.key === 'Escape') {
                                onCancelEdit();
                              }
                            }}
                          />
                          <button
                            onClick={() => onConfirmEdit(entryKey)}
                            className="p-0.5 text-green-600 hover:text-green-800"
                            title="Confirmer"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={onCancelEdit}
                            className="p-0.5 text-red-600 hover:text-red-800"
                            title="Annuler"
                          >
                            <XCircleIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <span className={entry.correctedImmatriculation ? 'text-[10px] text-gray-400 line-through' : ''}>
                            {entry.immatriculation || '-'}
                          </span>
                          {entry.correctedImmatriculation && (
                            <span className="text-[10px] font-medium text-green-600">
                              → {entry.correctedImmatriculation}
                            </span>
                          )}
                          <button
                            onClick={() => onStartEditImmat(entryKey, displayImmat)}
                            className="p-0.5 text-gray-400 hover:text-rgpd-blue-dark"
                            title="Corriger l'immatriculation"
                          >
                            <PencilIcon className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="max-w-[150px] px-3 py-2 text-xs text-gray-800">
                      <div className="flex items-center gap-1">
                        <span className="truncate" title={entry.name}>{entry.name}</span>
                        {entry.name && (
                          <button
                            onClick={() => handleCopy(entry.name, `name-${entryKey}`)}
                            className={`shrink-0 p-0.5 transition-colors ${
                              copiedKey === `name-${entryKey}`
                                ? 'text-green-600'
                                : 'text-gray-400 hover:text-rgpd-blue-dark'
                            }`}
                            title={copiedKey === `name-${entryKey}` ? 'Copié !' : 'Copier le nom'}
                          >
                            <ClipboardDocumentIcon className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="max-w-[180px] px-3 py-2 text-xs text-gray-600">
                      <div className="flex items-center gap-1">
                        <span className="truncate" title={address || '-'}>
                          {address || <span className="text-gray-400">-</span>}
                        </span>
                        {address && (
                          <button
                            onClick={() => handleCopy(address, `address-${entryKey}`)}
                            className={`shrink-0 p-0.5 transition-colors ${
                              copiedKey === `address-${entryKey}`
                                ? 'text-green-600'
                                : 'text-gray-400 hover:text-rgpd-blue-dark'
                            }`}
                            title={copiedKey === `address-${entryKey}` ? 'Copié !' : 'Copier l\'adresse'}
                          >
                            <ClipboardDocumentIcon className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="max-w-[100px] truncate px-3 py-2 text-xs text-gray-600" title={entry.reason || '-'}>
                      {entry.reason || '-'}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {entry.decision === 'force' && entry.forcedType ? (
                        <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[9px] font-medium text-blue-700">
                          {coproTypes[entry.forcedType as keyof typeof coproTypes]}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => onDecision(entryKey, 'force', 1)}
                          className={`flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                            entry.decision === 'force'
                              ? 'bg-green-600 text-white'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          Forcer copro
                        </button>

                        <button
                          onClick={() => onDecision(entryKey, 'skip')}
                          className={`rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                            entry.decision === 'skip'
                              ? 'bg-red-600 text-white'
                              : 'bg-red-100 text-red-700 hover:bg-red-200'
                          }`}
                        >
                          Ignorer
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="flex h-full items-center justify-center px-4 py-6 text-center text-sm text-gray-500">
            Aucune entrée non trouvée dans le RNIC à traiter. Vous pouvez passer à l'étape suivante.
          </div>
        )}
      </div>
    </div>
  );
};

export default TypeSelectionStep;
